import { AppWrapper } from "context/state";
import { register, unregister } from "next-offline/runtime";
import { AppProps } from "next/app";
import { useRouter } from "next/router";
import NProgress from "nprogress";
import { useEffect, useState } from "react";
import ReactGA from "react-ga";
import { QueryClient, QueryClientProvider } from "react-query";
import { createWebStoragePersistor } from "react-query/createWebStoragePersistor-experimental";
import { persistQueryClient } from "react-query/persistQueryClient-experimental";
import { Provider } from "react-redux";
import { ToastContainer } from 'react-toastify';
import IPageConfig from "rules/interface/config/config.interface";
import { useStore } from "../redux/store";

import { DirectionProvider, Loader, MantineProvider } from "@mantine/core";
import TrackSession from "components/track-session";
import mantineTheme from "constants/theme";
import visitedPageService from "services/visited-page.service";
import '@mantine/core/styles.css';
import "nprogress/nprogress.css";
import "scss/_colors.scss";
import "scss/carousel.scss";
import "scss/index.scss";
import "scss/menu.scss";
import "scss/pagination.scss";
import "scss/profile.scss";
import "scss/react-toastify.scss";
import "scss/select.scss";
import "../public/css/normalize.css";


// react-query config
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3, // retry after 3times error
      suspense: false,
      cacheTime: 1000 * 60 * 60 * 24, // 1 day cache time
      refetchOnReconnect: true,
      refetchOnWindowFocus: false,
    },
  },
});

const localStoragePersistor = createWebStoragePersistor({
  storage: typeof window !== "undefined" && window.localStorage,
});
// react-query persist
persistQueryClient({
  queryClient,
  persistor: localStoragePersistor,
});


type TPageProps = AppProps & {
  Component: JSX.Element & {
    config: IPageConfig
  }
  pageProps: any
}


const MyApp = ({ Component, pageProps }: TPageProps) => {
  const Layout = Component.config.layout;
  const router = useRouter();
  const store = useStore(pageProps.initialReduxState);

  const [history, setHistory] = useState([]);

  // set prev history
  useEffect(() => {
    setHistory((prev) => [...prev, window.location.href]);
    history?.length > 0 && localStorage.setItem("prevPath", history.pop());


    if (!visitedPageService.visited(router.asPath)) {
      visitedPageService.setPage(router.asPath)
    }
  }, [router.asPath]);

  useEffect(() => {
    // set initial key
    ReactGA.initialize(process.env.NEXT_PUBLIC_ANALYTICS);

    if (pageProps.statusCode == 404 && router.asPath.includes("/contractors")) {
      setTimeout(() => {
        router.push("/contractors");
      }, 2000);
    }

    // install service-worker in production mode
    process.env.NODE_ENV === "production" && register("/sw.js");

    // show navbar with NProgress when route change
    router.events.on("routeChangeStart", function () {
      NProgress.start();
    });
    router.events.on("routeChangeComplete", function () {
      NProgress.done();
    });
    router.events.on("routeChangeError", function () {
      NProgress.remove();
    });

    return () => {
      unregister();

      router.events.off("routeChangeStart", function () {
        NProgress.start();
      });
      router.events.off("routeChangeComplete", function () {
        NProgress.done();
      });
      router.events.off("routeChangeError", function () {
        NProgress.remove();
      });
    };
  }, []);


  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) {
    return <MantineProvider theme={mantineTheme}><Loader /></MantineProvider>;
  }


  return (
    <AppWrapper>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <DirectionProvider initialDirection="rtl">
            <MantineProvider theme={mantineTheme}>
              <Layout config={Component.config}>
                <Component {...pageProps} />

                <ToastContainer
                  position="bottom-center"
                  theme="dark"
                  autoClose={5000}
                  toastStyle={{
                    backgroundColor: "rgba(56, 49, 38, 0.95)"
                  }}
                  rtl
                />

              </Layout>
            </MantineProvider>
          </DirectionProvider>

          <TrackSession />

        </QueryClientProvider>
      </Provider>
    </AppWrapper>
  );
};

export default MyApp;
